import React, { useEffect, useMemo, useState } from "react"
import { useController } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { useEvent } from "shared/lib/useEvent"
import { RJSFSchema } from "shared/types/RJSF"
import { SelectOption } from "shared/types/Select"
import { Select } from "shared/ui/input/select/select/Select"

import { getDefaultFormData } from "entities/dim-configuration/lib/getDefaultFormData"

import { RJSFFormObjectField } from "./RJSFFormObjectField"
import { RJSFFormObjectFieldWrapperFactory } from "./RJSFFormObjectFieldWrapperFactory"
import { RJSFFormFieldProps } from "./types"

interface RJSFFormOneOfObjectFieldProps {
  prefix?: string
  schema: RJSFSchema
  isDisabled?: boolean
  FieldInformationRenderer?: React.ComponentType<RJSFFormFieldProps>
}
export const RJSFFormOneOfObjectField = React.memo(
  ({ schema, prefix, isDisabled }: RJSFFormOneOfObjectFieldProps) => {
    const { t } = useTranslation()
    const { field } = useController({ name: prefix || "root" })
    const [selectedSchemaIndex, setSelectedSchemaIndex] = useState(
      schema.defaultIndex || 0
    )
    const selectedSchema = schema.oneOf?.[selectedSchemaIndex]

    const handleChange = useEvent((next: number | null) => {
      setSelectedSchemaIndex(next ?? 0)
      field.onChange(getDefaultFormData(schema.oneOf?.[next ?? 0]))
    })

    useEffect(() => {
      setSelectedSchemaIndex(schema.defaultIndex ?? 0)
      field.onChange(schema.defaultIndex ?? 0)
    }, [schema.defaultIndex])

    const options: SelectOption<number>[] = useMemo(() => {
      if (!schema.oneOf) {
        return []
      }
      return schema.oneOf.map((subSchema, index) => ({
        value: index,
        label: subSchema.title || index.toString()
      }))
    }, [schema.oneOf])

    return (
      <RJSFFormObjectFieldWrapperFactory
        schema={schema}
        className="one-of-field"
        valueClassName="one-of-field"
      >
        <Select.Root label={schema.title || t("JSONSchema.OneOfSelect")}>
          <Select.Label />
          <Select.Input
            value={selectedSchemaIndex}
            options={options}
            onChange={handleChange}
            disabled={isDisabled}
          />
        </Select.Root>
        {selectedSchema ? (
          <RJSFFormObjectField
            path={prefix}
            schema={selectedSchema}
            isDisabled={isDisabled}
          />
        ) : null}
      </RJSFFormObjectFieldWrapperFactory>
    )
  }
)
